import { useEffect, useState, type ChangeEvent } from 'react';
import { api } from '../api';
import { shortDate } from '../format';
import { compressImage } from '../image';
import type { ConditionAnomaly, ItemState } from '../types';

const STATE_LABEL: Record<ItemState, string> = {
  VERY_GOOD: 'Très bon', GOOD: 'Bien', AVERAGE: 'Moyen', POOR: 'Mauvais',
};

/**
 * Fiche complète d'une anomalie : le constat tel qu'observé (date, pièce,
 * commentaire, photo), et en dessous la réparation, modifiable même après
 * avoir cliqué « Réparé ». Une date ou un nom mal saisis n'obligent plus à
 * annuler puis refaire : la déclaration active se corrige en place tant
 * qu'aucun état des lieux de sortie ne s'y est appuyé.
 */
export function AnomalyDetailModal({
  item, basePath, allowReopen, onClose, onSaved,
}: {
  item: ConditionAnomaly;
  basePath: string;
  allowReopen: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [performedBy, setPerformedBy] = useState(item.performedBy);
  const [performedOn, setPerformedOn] = useState(item.performedOn ?? '');
  const [description, setDescription] = useState(item.resolutionDescription);
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [addingPhoto, setAddingPhoto] = useState(false);
  // L'URL d'une photo en cours de suppression sert de clé : ça bloque son
  // propre bouton pendant l'appel, sans geler le reste de la fiche.
  const [removingPhoto, setRemovingPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  // Ne réinitialise le formulaire qu'à l'ouverture d'une anomalie différente :
  // un rechargement de la liste pendant la saisie ne doit pas effacer ce que
  // l'utilisateur est en train de corriger.
  useEffect(() => {
    setPerformedBy(item.performedBy);
    setPerformedOn(item.performedOn ?? '');
    setDescription(item.resolutionDescription);
    setPendingPhotos([]);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  async function save(): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await api(`${basePath}/${item.id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ description: description.trim(), performedBy: performedBy.trim(), performedOn: performedOn || null }),
      });
      for (const file of pendingPhotos) {
        // Les photos de téléphone récentes pèsent plusieurs Mo, bien au-delà
        // de la limite de 6 Mo acceptée par le serveur.
        const compressed = await compressImage(file);
        const form = new FormData();
        form.append('photo', compressed, `photo-${Date.now()}.jpg`);
        await api(`${basePath}/${item.id}/repair-photos`, { method: 'POST', body: form });
      }
      setPendingPhotos([]);
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  /** Partagé par les deux commandes : elles ne diffèrent que par leur `capture`. */
  function collectPhotos(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? []);
    if (files.length) setPendingPhotos((current) => [...current, ...files]);
    event.target.value = '';
  }

  /**
   * Photo du constat, envoyée aussitôt : contrairement à la réparation, cette
   * section n'a pas de bouton d'enregistrement où la rattacher, et une photo
   * reçue d'un locataire n'attend rien d'autre pour rejoindre le dossier.
   */
  async function addConstatPhotos(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    setAddingPhoto(true);
    setError('');
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        const form = new FormData();
        form.append('photo', compressed, `constat-${Date.now()}.jpg`);
        await api(`${basePath}/${item.id}/photos`, { method: 'POST', body: form });
      }
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Ajout de la photo impossible.");
    } finally {
      setAddingPhoto(false);
    }
  }

  /**
   * Retrait d'une photo mal jointe (mauvaise chambre, doublon, cliché flou) —
   * né du cas réel d'Aurélie, qui n'avait jusqu'ici pas d'autre recours que
   * d'annuler toute la réparation pour effacer une seule photo malvenue.
   * `endpoint` distingue une photo de constat d'une photo de réparation,
   * la seconde étant verrouillée dès que la réparation est annulée.
   */
  async function removePhoto(endpoint: 'photos' | 'repair-photos', url: string): Promise<void> {
    const documentId = url.split('/').pop() ?? '';
    if (!documentId) return;
    setRemovingPhoto(url);
    setError('');
    try {
      await api(`${basePath}/${item.id}/${endpoint}/${documentId}`, { method: 'DELETE' });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression de la photo impossible.');
    } finally {
      setRemovingPhoto(null);
    }
  }

  async function revert(): Promise<void> {
    setReverting(true);
    setError('');
    try {
      await api(`${basePath}/${item.id}/reopen`, { method: 'POST' });
      await onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Annulation impossible.');
    } finally {
      setReverting(false);
    }
  }

  async function remove(): Promise<void> {
    if (!window.confirm('Retirer définitivement ce signalement ?')) return;
    setDeleting(true);
    setError('');
    try {
      await api(`${basePath}/${item.id}`, { method: 'DELETE' });
      await onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Suppression impossible.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={`${item.roomLabel} · ${item.itemLabel}`}
      onClick={onClose}
    >
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">{item.roomName ? `Chambre ${item.roomName}` : 'Partie commune'} · {item.roomLabel}</p>
            <h2>{item.itemLabel}</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Fermer" onClick={onClose}>×</button>
        </div>

        <section className="modal-section">
          <p className="eyebrow">Constat</p>
          <p className="record-line">
            <span className={`status ${item.state === 'POOR' || item.state === 'AVERAGE' ? 'draft' : 'signed'}`}>{STATE_LABEL[item.state]}</span>
            <span className="muted">
              {item.reportedAt
                ? `signalé le ${shortDate(item.reportedAt)}${item.reportedBy ? ` par ${item.reportedBy}` : ''}`
                : item.observedOn
                  ? `constaté le ${shortDate(item.observedOn)} (${item.observedType === 'ENTRY' ? 'entrée' : 'sortie'})`
                  : 'origine inconnue'}
            </span>
          </p>
          {item.comment && <p>{item.comment}</p>}
          {item.photoDocumentUrl ? (
            <a href={item.photoDocumentUrl} target="_blank" rel="noreferrer" className="modal-photo-link">
              <img src={item.photoDocumentUrl} alt={`Constat : ${item.itemLabel}`} />
            </a>
          ) : (
            item.reportPhotoUrls.length === 0 && <p className="muted">Aucune photo au constat.</p>
          )}
          {item.reportPhotoUrls.length > 0 && (
            <div className="constat-photos">
              {item.reportPhotoUrls.map((url, index) => (
                <div className="photo-thumb" key={url}>
                  <a href={url} target="_blank" rel="noreferrer" className="modal-photo-link">
                    <img src={url} alt={`Photo ${index + 1} : ${item.itemLabel}`} />
                  </a>
                  <button
                    type="button"
                    className="icon-button photo-remove"
                    aria-label="Supprimer cette photo"
                    disabled={removingPhoto === url}
                    onClick={() => void removePhoto('photos', url)}
                  >×</button>
                </div>
              ))}
            </div>
          )}
          <div className="photo-actions">
            <label className={addingPhoto ? 'button secondary compact disabled' : 'button secondary compact'}>
              Prendre une photo
              <input type="file" accept="image/*" capture="environment" hidden disabled={addingPhoto} onChange={(event) => void addConstatPhotos(event)} />
            </label>
            <label className={addingPhoto ? 'button secondary compact disabled' : 'button secondary compact'}>
              Ajouter des photos
              <input type="file" accept="image/*" multiple hidden disabled={addingPhoto} onChange={(event) => void addConstatPhotos(event)} />
            </label>
            {addingPhoto && <span className="muted">Envoi…</span>}
          </div>
          <p className="muted hint-line">
            Pour une photo reçue d'un locataire, ou prise en passant. La photo de l'état des lieux, elle, n'est
            jamais remplacée.
          </p>
        </section>

        <section className="modal-section">
          <p className="eyebrow">Réparation</p>
          <div className="form-grid">
            <label>Qui est intervenu
              <input value={performedBy} onChange={(event) => setPerformedBy(event.target.value)} placeholder="nom de l'intervenant" />
            </label>
            <label>Date de l'intervention
              <input type="date" value={performedOn} onChange={(event) => setPerformedOn(event.target.value)} />
            </label>
            <label className="span-2">Ce qui a été fait
              <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} placeholder="matelas nettoyé, housse posée…" />
            </label>
          </div>

          {item.repairPhotoUrls.length > 0 && (
            <div className="constat-photos">
              {item.repairPhotoUrls.map((url, index) => (
                <div className="photo-thumb" key={url}>
                  <a href={url} target="_blank" rel="noreferrer" className="modal-photo-link">
                    <img src={url} alt={`Photo de réparation ${index + 1} : ${item.itemLabel}`} />
                  </a>
                  <button
                    type="button"
                    className="icon-button photo-remove"
                    aria-label="Supprimer cette photo"
                    disabled={removingPhoto === url}
                    onClick={() => void removePhoto('repair-photos', url)}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          {/* Deux commandes explicites plutôt qu'un seul champ : selon le
              téléphone, un champ sans `capture` ouvre parfois un choix
              « appareil photo ou fichiers », parfois directement le gestionnaire
              de fichiers. Avec `capture` c'est l'inverse : l'appareil photo, et
              plus de galerie. Aucune des deux variantes ne garantit les deux
              chemins, d'où un bouton pour chacun. */}
          <div className="photo-actions">
            <label className="button secondary compact">
              Prendre une photo
              <input type="file" accept="image/*" capture="environment" hidden onChange={collectPhotos} />
            </label>
            <label className="button secondary compact">
              Importer des photos
              <input type="file" accept="image/*" multiple hidden onChange={collectPhotos} />
            </label>
          </div>
          {pendingPhotos.length > 0 && (
            <ul className="pending-photos">
              {pendingPhotos.map((file, index) => (
                <li key={`${file.name}-${index}`}>
                  {file.name}
                  <button type="button" className="icon-button" aria-label="Retirer" onClick={() => setPendingPhotos((current) => current.filter((_, i) => i !== index))}>×</button>
                </li>
              ))}
            </ul>
          )}
          {item.resolvedAt && item.repairPhotoUrls.length === 0 && pendingPhotos.length === 0 && (
            <p className="warn-text">
              Sans photo, la réparation est déclarée mais pas démontrée : elle rendra une retenue difficile à
              défendre si le dégât revient à la sortie.
            </p>
          )}

          {error && <div className="inline-message error" role="alert">{error}</div>}

          <div className="modal-actions">
            {item.observedType === null && (
              <button type="button" className="text-button danger" disabled={deleting || busy} onClick={() => void remove()}>
                {deleting ? 'Suppression…' : 'Supprimer ce signalement'}
              </button>
            )}
            {item.resolvedAt && allowReopen && (
              <button type="button" className="button secondary compact" disabled={reverting || busy} onClick={() => void revert()}>
                {reverting ? 'Annulation…' : 'Annuler la réparation'}
              </button>
            )}
            <button type="button" className="button primary compact" disabled={busy || reverting} onClick={() => void save()}>
              {busy ? 'Enregistrement…' : item.resolvedAt ? 'Enregistrer les modifications' : 'Réparé'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
